import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import type { CommissionTierRates } from '@/marketing/models/voucher';
import { VOUCHER_KEYS } from '@/marketing/infrastructure/influencer/voucher/utils/keys';
import { VOUCHER_MUTATIONS, VOUCHER_QUERIES } from '@/marketing/infrastructure/influencer/voucher/utils/queries';

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;
const HTTP_CONFLICT = 409;

const isVersionConflict = (error: unknown): boolean =>
  (error as { response?: { status?: number } }).response?.status === HTTP_CONFLICT;

interface TierField {
  key: 'couponPercent' | 'utmPercent' | 'couponUtmPercent';
  label: string;
  hint: string;
}

const TIER_FIELDS: TierField[] = [
  { key: 'couponPercent', label: 'Coupon only', hint: 'Code at checkout, no link' },
  { key: 'utmPercent', label: 'Link only', hint: 'Referral link, no code' },
  { key: 'couponUtmPercent', label: 'Link + coupon', hint: 'Both signals — the top tier' },
];

type TierDraft = Record<TierField['key'], string>;

const draftOf = (rates: CommissionTierRates): TierDraft => ({
  couponPercent: String(rates.couponPercent),
  utmPercent: String(rates.utmPercent),
  couponUtmPercent: String(rates.couponUtmPercent),
});

const isValidPercent = (value: string): boolean => {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) && MIN_PERCENT <= parsed && parsed <= MAX_PERCENT;
};

// Read-only by default so the card sits next to the KPI row without looking like a form;
// Edit swaps the three values for inputs in place.
function TierRatesContent({ rates }: { rates: CommissionTierRates }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TierDraft>(() => draftOf(rates));

  const saveMutation = useMutation({
    ...VOUCHER_MUTATIONS.setTierRates(),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: VOUCHER_KEYS.tierRates() });
      setDraft(draftOf(saved));
      setIsEditing(false);
      toast.success('Program tier defaults saved');
    },
    // A stale version means another admin saved first: reload the current revision and let them re-apply.
    onError: async (error) => {
      if (isVersionConflict(error)) {
        await queryClient.invalidateQueries({ queryKey: VOUCHER_KEYS.tierRates() });
        setIsEditing(false);
        toast.error('The tier defaults changed while you were editing — reloaded the current values.');
        return;
      }
      toast.error(extractErrorMessage(error, 'Failed to save the tier defaults'));
    },
  });

  const isEveryFieldValid = TIER_FIELDS.every((field) => isValidPercent(draft[field.key]));
  const hasChanges = TIER_FIELDS.some((field) => Number(draft[field.key]) !== rates[field.key]);

  const startEditing = () => {
    setDraft(draftOf(rates));
    setIsEditing(true);
  };

  const save = () => {
    saveMutation.mutate({
      expectedVersion: rates.version,
      couponPercent: Number(draft.couponPercent),
      utmPercent: Number(draft.utmPercent),
      couponUtmPercent: Number(draft.couponUtmPercent),
    });
  };

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-8 tw-gap-y-3">
      {TIER_FIELDS.map((field) => (
        <div key={field.key} className="tw-min-w-[120px]">
          <div className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            {field.label}
          </div>
          {isEditing ? (
            <div className="tw-mt-1 tw-flex tw-items-baseline tw-gap-1">
              <Input
                value={draft[field.key]}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, [field.key]: event.target.value.replace(/[^0-9.]/g, '') }))
                }
                inputMode="decimal"
                className="tw-h-8 tw-w-16 tw-text-right tw-tabular-nums"
              />
              <span className="tw-text-sm tw-text-muted-foreground">%</span>
            </div>
          ) : (
            <div className="tw-mt-1 tw-text-2xl tw-font-bold tw-leading-none tw-text-foreground">
              {rates[field.key]}
              <span className="tw-text-sm tw-font-normal tw-text-muted-foreground">%</span>
            </div>
          )}
          <div className="tw-mt-1 tw-text-xs tw-text-muted-foreground">{field.hint}</div>
        </div>
      ))}
      <div className="tw-ml-auto tw-flex tw-gap-2">
        {isEditing ? (
          <>
            <Button variant="ghost" size="sm" disabled={saveMutation.isPending} onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!isEveryFieldValid || !hasChanges || saveMutation.isPending} onClick={save}>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={startEditing}>
            <Pencil className="tw-mr-1.5 tw-h-3.5 tw-w-3.5" />
            Edit
          </Button>
        )}
      </div>
    </div>
  );
}

// Program-level tier defaults (SSD-5811): codes without custom rates inherit these.
// Campaigns slot between the two levels when they arrive (code override > campaign > program).
export function CommissionTierCard() {
  const { data: rates, isPending, isError } = useQuery(VOUCHER_QUERIES.tierRates());

  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-px-4 tw-py-3">
      <div className="tw-flex tw-items-baseline tw-gap-2">
        <h2 className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Program tier defaults
        </h2>
        <span className="tw-text-xs tw-text-muted-foreground">
          Codes without custom rates inherit these
        </span>
      </div>
      <div className="tw-mt-2">
        {isPending && <div className="tw-text-sm tw-text-muted-foreground">Loading tier defaults…</div>}
        {isError && <div className="tw-text-sm tw-text-muted-foreground">Failed to load the tier defaults.</div>}
        {rates && <TierRatesContent key={rates.version} rates={rates} />}
      </div>
    </div>
  );
}
